// Optimizer utility functions — extracted from OptimizerDashboard.js
// Canonical paths only: player.info.*, player.stats.season.*, player.league_context.*
import { isPitcherPosition } from '../../../utils/playerClassification';
import { dynastyTokens } from '../../../services/colorService';

// DD-RO-CLASSAUDIT-0725 T3-21: ONE acceptance-color scale for EVERY optimizer surface. Previously three
// components used three different cutoff sets (Flyout 0.6/0.4, Recs 0.30/0.20, Detail 0.40/0.20) — the same
// acceptance % showed a different color per surface. The green threshold ties to the backend's best-overall
// gate (_BEST_OVERALL_MIN_ACCEPTANCE = 0.4): green = at/above the acceptance the engine would feature a deal
// at, amber = plausible, red = unlikely. Single source, calibrated to the unified acceptance model (T1-6).
// DD-RO-MOBILE-FIELD-0728 DUPLICATION: these two cutoffs are ALSO declared in the mobile field view
// (`components/mobile/pages/optimizer/MobileFieldView.js` ACCEPT_GREEN/ACCEPT_AMBER) because §14.0 forbids the
// mobile tree from importing desktop code. If either cutoff ever moves, it MUST move in both places.
export const ACCEPT_GREEN = 0.4;   // == backend _BEST_OVERALL_MIN_ACCEPTANCE
export const ACCEPT_AMBER = 0.2;
export const acceptColor = (pct) => {
  const c = dynastyTokens.colors;
  return (pct >= ACCEPT_GREEN) ? c.success : (pct >= ACCEPT_AMBER) ? c.warning : c.error;
};

// DD-RO-FA-0726: ONE position-label source for every optimizer surface. Never render the primary MLB
// position alone — that hid effective eligibility and made a correct 2B-eligible OF look like a wrong
// result. Order of preference: (1) the backend's eligibility_display (qualifying slot first, e.g.
// "2B/OF" — set on FA/alternatives, trade-side summaries, follow-ups, personalized); (2) derived from a
// full player's effective_eligibility; (3) primary position only as a last resort for a legacy payload.
const _DISPLAY_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'OF', 'P', 'DH'];
export const eligibilityLabel = (elig) => {
  if (!elig || !elig.length) return '';
  const set = new Set(elig);
  return _DISPLAY_POSITIONS.filter((p) => set.has(p)).join('/');
};
export const positionLabel = (player) =>
  player?.eligibility_display
  || eligibilityLabel(player?.league_context?.roster?.effective_eligibility)
  || player?.info?.position || player?.position || '—';

// Strip "_N" suffix from roster position: "3B_0" -> "3B", "OF_1" -> "OF", "P_6" -> "P"
export const getBasePosition = (pos) => {
  if (!pos) return null;
  return pos.replace(/_\d+$/, '');
};

// Determine if a player is a pitcher (canonical helper — Bible §4.5.9.1)
export const isPitcher = (player) => isPitcherPosition(player.info?.position);

// Format contract salary for display
export const fmtSalary = (player) => {
  const sal = player.league_context?.financial?.contract_salary;
  return sal != null ? `$${sal}` : '';
};

// Format key stat — OPS for hitters, ERA for pitchers (single-stat display)
export const fmtStat = (player) => {
  if (isPitcher(player)) {
    const era = player.stats?.season?.pitching?.era;
    return era != null ? `ERA ${Number(era).toFixed(2)}` : '';
  }
  const ops = player.stats?.season?.hitting?.ops;
  return ops != null ? `OPS ${Number(ops).toFixed(3).replace(/^0/, '')}` : '';
};

// Stat abbreviation → DB field name (matches backend STAT_MAPPING)
const STAT_FIELD_MAP = {
  R: 'runs', HR: 'home_runs', RBI: 'rbi', SB: 'stolen_bases',
  AVG: 'batting_avg', OPS: 'ops', OBP: 'obp', SLG: 'slg',
  H: 'hits', BB: 'walks', '2B': 'doubles', '3B': 'triples',
  W: 'wins', SV: 'saves', SO: 'strikeouts_pitched', K: 'strikeouts_pitched',
  QS: 'quality_starts', ERA: 'era', WHIP: 'whip', HLD: 'holds',
  IP: 'innings_pitched', BS: 'blown_saves',
};

// Rate stats that need special formatting (no leading zero, or 2 decimals)
const RATE_STATS_SET = new Set(['AVG', 'OBP', 'SLG', 'OPS']);
const PITCHING_RATE_STATS = new Set(['ERA', 'WHIP']);

// Format a single stat value for display
export const fmtStatValue = (abbrev, value) => {
  if (value == null) return '—';
  const num = Number(value);
  if (isNaN(num)) return '—';
  if (RATE_STATS_SET.has(abbrev)) return num.toFixed(3).replace(/^0/, '');
  if (PITCHING_RATE_STATS.has(abbrev)) return num.toFixed(2);
  return String(Math.round(num));
};

// Get all category stats for a player given the league's scoring categories
// Returns [{abbrev, value, formatted}] for the relevant type (hitting or pitching)
export const getPlayerCategoryStats = (player, categories) => {
  if (!categories) return [];
  const pitcher = isPitcher(player);
  const catList = pitcher ? (categories.pitching || []) : (categories.hitting || []);
  const statType = pitcher ? 'pitching' : 'hitting';
  const seasonStats = player.stats?.season?.[statType] || {};

  return catList.map(abbrev => {
    const field = STAT_FIELD_MAP[abbrev] || abbrev.toLowerCase();
    const value = seasonStats[field];
    return {
      abbrev,
      value,
      formatted: fmtStatValue(abbrev, value),
    };
  });
};
